import type { SetStateAction } from "react"

import type { ChatAppResponse } from "@/api/models"
import type { ChatSlice, SessionState } from "@/stores/slices/chat-slice"
import type { StoreApi, UseBoundStore } from "zustand"

export type ChatStore = UseBoundStore<StoreApi<ChatSlice>>

export type AbortedRequest =
  | [question: string, response: ChatAppResponse]
  | [question: string, response: undefined]

export type ChatRequestControl = {
  abortController: AbortController
  requestGenerationId: string
  requestToken: symbol
}

type ChatRequestTarget = {
  requestToken?: symbol
  sessionId: string
  store: ChatStore
}

type SessionRequestEntry = {
  aborted?: AbortedRequest
  completionNotificationToken?: symbol
  control?: ChatRequestControl
  ownerToken?: symbol
  requestGenerationId?: string
}

type StoreRequestRegistry = {
  // One O(1) tombstone per successful deletion, scoped to this WeakMap store.
  // This is the only record of deleted chats: every useChatHistory instance on
  // the store reads it (#3524). Frames have no safe TTL; only an explicit new
  // request may clear its id (see beginChatRequest).
  disposedSessions: Set<string>
  // Unlike the tombstone, this survives same-id recreation so callbacks that
  // started before deletion cannot mistake the replacement session for theirs.
  disposalGenerations: Map<string, number>
  listeners: Set<() => void>
  sessions: Map<string, SessionRequestEntry>
  // Process-local identity of this store. With the disposal generation it
  // names a revived chat's own query identity (see getChatSessionQueryScope).
  storeId: number
}

// The store function is the lifetime boundary: Explorer route remounts reuse it,
// while tests/users with different store instances cannot see each other's state.
const registries = new WeakMap<ChatStore, StoreRequestRegistry>()
let nextStoreId = 0

function getRegistry(store: ChatStore): StoreRequestRegistry {
  const existing = registries.get(store)
  if (existing) return existing

  const created: StoreRequestRegistry = {
    disposedSessions: new Set(),
    disposalGenerations: new Map(),
    listeners: new Set(),
    sessions: new Map(),
    storeId: ++nextStoreId,
  }
  registries.set(store, created)
  return created
}

function getEntry(store: ChatStore, sessionId: string): SessionRequestEntry {
  const registry = getRegistry(store)
  const existing = registry.sessions.get(sessionId)
  if (existing) return existing

  const created: SessionRequestEntry = {}
  registry.sessions.set(sessionId, created)
  return created
}

function emit(store: ChatStore) {
  for (const listener of getRegistry(store).listeners) listener()
}

/**
 * Starts a new request owner for `sessionId`.
 *
 * Revive rule (#3524): this is the only function that clears a deletion
 * tombstone. Production calls it only from `sendChatRequest` in use-chat.ts
 * (legacy and durable Explorer paths), which runs for a user send, Retry or
 * Continue on the store's current session. Sending into a deleted id therefore
 * deliberately recreates that chat for every instance on the store.
 *
 * Nothing else revives a deleted id: selection and route sync
 * (`handleSelectSession`), message hydration, `initSession`, late or stray
 * stream frames, `setChatRequestAborted` and passive generation adoption
 * (`adoptChatRequestGeneration`) all leave the tombstone in place.
 *
 * Reviving does not reset the disposal generation, and disposal dropped the
 * old request entry. Tokens, generations, selections and cached server rows
 * captured before the deletion therefore stay stale and cannot write into the
 * replacement session.
 */
export function beginChatRequest(
  store: ChatStore,
  sessionId: string,
  abortController: AbortController
): ChatRequestControl {
  getRegistry(store).disposedSessions.delete(sessionId)
  const entry = getEntry(store, sessionId)
  const previousControl = entry.control
  const control = {
    abortController,
    requestGenerationId: crypto.randomUUID(),
    requestToken: Symbol("chat-request"),
  }

  // Establish the new owner before aborting the previous transport. Any abort
  // callback that writes synchronously is therefore already stale and rejected.
  entry.ownerToken = control.requestToken
  entry.requestGenerationId = control.requestGenerationId
  entry.control = control
  entry.aborted = undefined
  emit(store)
  previousControl?.abortController.abort()

  return control
}

export function getChatRequestControl(
  store: ChatStore,
  sessionId: string
): ChatRequestControl | undefined {
  return getRegistry(store).sessions.get(sessionId)?.control
}

export function getChatRequestOwnerToken(store: ChatStore, sessionId: string): symbol | undefined {
  return getRegistry(store).sessions.get(sessionId)?.ownerToken
}

export function getChatRequestGenerationId(
  store: ChatStore,
  sessionId: string
): string | undefined {
  return getRegistry(store).sessions.get(sessionId)?.requestGenerationId
}

export function isChatRequestGenerationCurrent(
  store: ChatStore,
  sessionId: string,
  requestGenerationId: string
): boolean {
  return getChatRequestGenerationId(store, sessionId) === requestGenerationId
}

export function adoptChatRequestGeneration(
  store: ChatStore,
  sessionId: string,
  request:
    | string
    | {
        requestGenerationId: string
        expectedRequestGenerationId: string | undefined
      }
): boolean {
  const requestGenerationId = typeof request === "string" ? request : request.requestGenerationId
  if (!requestGenerationId || isChatRequestSessionDisposed(store, sessionId)) return false
  const entry = getEntry(store, sessionId)
  if (entry.requestGenerationId === requestGenerationId) return true
  if (
    entry.requestGenerationId &&
    entry.requestGenerationId !== requestGenerationId &&
    (entry.control || entry.ownerToken)
  )
    return false
  if (
    typeof request !== "string" &&
    entry.requestGenerationId !== undefined &&
    entry.requestGenerationId !== requestGenerationId &&
    entry.requestGenerationId !== request.expectedRequestGenerationId
  )
    return false
  entry.requestGenerationId = requestGenerationId
  // Notify subscribers (e.g. Deep reattach effect) so a late poll/hydration
  // adopt can retry after an earlier no-op that lacked a generation fence.
  emit(store)
  return true
}

export function chatRequestOwnsSession(
  store: ChatStore,
  sessionId: string,
  requestToken: symbol
): boolean {
  return getRegistry(store).sessions.get(sessionId)?.ownerToken === requestToken
}

export function claimChatRequestCompletionNotification(
  store: ChatStore,
  sessionId: string,
  requestToken: symbol
): boolean {
  const entry = getRegistry(store).sessions.get(sessionId)
  if (entry?.ownerToken !== requestToken) return false
  if (entry.completionNotificationToken === requestToken) return false

  entry.completionNotificationToken = requestToken
  return true
}

export function writeChatRequestState(
  target: ChatRequestTarget & { requestToken: symbol },
  partial: Partial<SessionState>
): boolean {
  const { requestToken, sessionId, store } = target
  if (!chatRequestOwnsSession(store, sessionId, requestToken)) return false
  store.getState().updateSession(sessionId, partial)
  return true
}

export function finishChatRequestControl(
  store: ChatStore,
  sessionId: string,
  requestToken: symbol
) {
  const entry = getRegistry(store).sessions.get(sessionId)
  // The transport is no longer stoppable, but keep ownerToken so the stream
  // handler's delayed settle callback can still write unless a newer turn wins.
  if (entry?.control?.requestToken === requestToken) entry.control = undefined
}

export function retireChatRequest(store: ChatStore, sessionId: string, requestToken: symbol) {
  const entry = getRegistry(store).sessions.get(sessionId)
  if (entry?.ownerToken !== requestToken) return
  entry.ownerToken = undefined
  entry.control = undefined
}

export function disposeChatRequestSession(store: ChatStore, sessionId: string) {
  const registry = getRegistry(store)
  const entry = registry.sessions.get(sessionId)
  registry.disposedSessions.add(sessionId)
  registry.disposalGenerations.set(
    sessionId,
    (registry.disposalGenerations.get(sessionId) ?? 0) + 1
  )
  // Mark disposed and remove ownership before aborting. Abort listeners and
  // delayed transport callbacks can run synchronously, and must already see
  // the tombstone so they cannot recreate state for a deleted chat.
  if (entry) {
    registry.sessions.delete(sessionId)
    entry.control?.abortController.abort()
  }
  // Notify even when no request ever ran for this id: query consumers
  // subscribe to the tombstone and the disposal generation.
  emit(store)
}

export function isChatRequestSessionDisposed(store: ChatStore, sessionId: string): boolean {
  return getRegistry(store).disposedSessions.has(sessionId)
}

/**
 * Query identity of a chat session in this store (#3524).
 *
 * `disposed` is true while the delete tombstone stands; consumers must not
 * fetch then. `incarnation` is undefined until the session's first successful
 * deletion, so a chat that was never deleted keeps its original query keys.
 * After a deletion it names the store and disposal generation. A revived chat
 * therefore reads from query keys that no fetch for the deleted chat ever
 * used, so a late commit of an old reply lands in the old query, not in the
 * replacement. Old queries are not purged; they expire by ordinary GC.
 */
export type ChatSessionQueryScope = {
  disposed: boolean
  generation: number
  incarnation: string | undefined
}

export function getChatSessionQueryScopeSnapshot(store: ChatStore, sessionId: string): string {
  const registry = getRegistry(store)
  const generation = registry.disposalGenerations.get(sessionId) ?? 0
  const disposed = registry.disposedSessions.has(sessionId)
  return `${disposed ? 1 : 0}:${generation}:${registry.storeId}`
}

export function parseChatSessionQueryScope(snapshot: string): ChatSessionQueryScope {
  const [disposed, generation, storeId] = snapshot.split(":")
  return {
    disposed: disposed === "1",
    generation: Number(generation),
    incarnation: generation === "0" ? undefined : `${storeId}:${generation}`,
  }
}

export function getChatSessionQueryScope(
  store: ChatStore,
  sessionId: string
): ChatSessionQueryScope {
  return parseChatSessionQueryScope(getChatSessionQueryScopeSnapshot(store, sessionId))
}

export function getChatRequestSessionDisposalGeneration(
  store: ChatStore,
  sessionId: string
): number {
  return getRegistry(store).disposalGenerations.get(sessionId) ?? 0
}

export function isChatRequestSessionDisposalGenerationCurrent(
  store: ChatStore,
  sessionId: string,
  generation: number
): boolean {
  return getChatRequestSessionDisposalGeneration(store, sessionId) === generation
}

export function setChatRequestAborted(
  target: ChatRequestTarget,
  value: SetStateAction<AbortedRequest | undefined>
): boolean {
  const { requestToken, sessionId, store } = target
  if (isChatRequestSessionDisposed(store, sessionId)) return false
  const entry = getEntry(store, sessionId)
  if (requestToken && entry.ownerToken !== requestToken) return false

  const nextValue = typeof value === "function" ? value(entry.aborted) : value
  if (nextValue === entry.aborted) return true

  entry.aborted = nextValue
  emit(store)
  return true
}

export function getChatRequestAborted(
  store: ChatStore,
  sessionId: string | null
): AbortedRequest | undefined {
  return sessionId ? getRegistry(store).sessions.get(sessionId)?.aborted : undefined
}

export function subscribeChatRequestRegistry(store: ChatStore, listener: () => void) {
  const listeners = getRegistry(store).listeners
  listeners.add(listener)
  return () => listeners.delete(listener)
}
